<script lang="ts">
	import { Button, Text, Toggle } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { real_id } from 'src/helpers/interface_helpers';
	import type { VentoraFanConfig, VentoraTempConfig } from 'src/types/ventora_fan';
	import VentoraFanNumericInput from './VentoraFanNumericInput.svelte';
	import {
		VENTORA_TEMP_OFF_DELAY_HINT,
		VENTORA_TEMP_SPEED_HINT,
		VENTORA_TEMP_THRESHOLD_HINT,
		VENTORA_TEMP_TRIGGER_PRIORITY_HINT,
	} from './ventora_fan_field_copy';
	import {
		defaultVentoraFanConfig,
		patchVentoraFanConfig,
		resolveVentoraFanConfig,
		VENTORA_FAN_COMPONENT_KEY,
		ventoraClamp,
		ventoraFiniteNumberFromInput,
		ventoraHasValidationErrors,
		ventoraNumericValidation,
		ventoraTempLimits,
		ventoraToNumber,
	} from './ventora_fan_settings_helpers';

	export let device;
	export let isOffline: boolean | undefined;
	export let test_setting: boolean | undefined;
	export let useNewLayout: boolean | undefined;

	const id = real_id(device.id);

	type TempNumericField = keyof Pick<VentoraTempConfig, 'speed' | 'thr' | 'off_delay'>;

	let loading = false;
	let dirty = false;
	let draft: VentoraTempConfig = { ...defaultVentoraFanConfig.temp };

	$: settingsStore = getDeviceSettings(device.id);
	$: config = resolveVentoraFanConfig(
		$settingsStore?.[VENTORA_FAN_COMPONENT_KEY] as Partial<VentoraFanConfig> | undefined,
	);

	$: if (!dirty) {
		draft = { ...config.temp };
	}

	$: speedValidation = ventoraNumericValidation(draft.speed, ...ventoraTempLimits.speed);
	$: thresholdValidation = ventoraNumericValidation(draft.thr, ...ventoraTempLimits.thr);
	$: offDelayValidation = ventoraNumericValidation(draft.off_delay, ...ventoraTempLimits.off_delay);
	$: hasValidationErrors = ventoraHasValidationErrors([
		speedValidation,
		thresholdValidation,
		offDelayValidation,
	]);

	const inputField = (field: TempNumericField, e: Event) => {
		dirty = true;
		const n = ventoraFiniteNumberFromInput((e.currentTarget as HTMLInputElement).value);
		if (n !== undefined) draft = { ...draft, [field]: n };
	};

	const blurField = (field: TempNumericField, e: Event) => {
		const [min, max] = ventoraTempLimits[field];
		const raw = (e.currentTarget as HTMLInputElement).value;
		draft = {
			...draft,
			[field]: ventoraClamp(ventoraToNumber(raw, draft[field]), min, max),
		};
	};

	const commitTempDraft = () => {
		draft = {
			...draft,
			speed: ventoraClamp(draft.speed, ...ventoraTempLimits.speed),
			thr: ventoraClamp(draft.thr, ...ventoraTempLimits.thr),
			off_delay: ventoraClamp(draft.off_delay, ...ventoraTempLimits.off_delay),
		};
	};

	const save = async () => {
		if (!dirty || hasValidationErrors) return;
		commitTempDraft();
		loading = true;
		const ok = await patchVentoraFanConfig(device, { temp: { ...draft } });
		if (ok) dirty = false;
		loading = false;
	};
</script>

<Text styleType="body-small" class="mb-m text-color-muted">
	{$format(VENTORA_TEMP_TRIGGER_PRIORITY_HINT.key, {
		default: VENTORA_TEMP_TRIGGER_PRIORITY_HINT.default,
	})}
</Text>

<Toggle
	size="m"
	class="mb-m"
	checked={draft.enable}
	on:click={() => {
		draft = { ...draft, enable: !draft.enable };
		dirty = true;
	}}
	id="toggle-ventora-temp-trigger"
>
	{$format('ventora_temp_enable', { default: 'Enable temperature trigger' })}
</Toggle>

<VentoraFanNumericInput
	_id="input-ventora-temp-speed"
	labelKey="ventora_temp_speed"
	labelDefault="Speed"
	hintKey={VENTORA_TEMP_SPEED_HINT.key}
	hintDefault={VENTORA_TEMP_SPEED_HINT.default}
	value={draft.speed}
	min={ventoraTempLimits.speed[0]}
	max={ventoraTempLimits.speed[1]}
	icon="fas fa-fan"
	validation={speedValidation}
	onInput={(e) => inputField('speed', e)}
	onBlur={(e) => blurField('speed', e)}
/>

<VentoraFanNumericInput
	_id="input-ventora-temp-threshold"
	labelKey="ventora_temp_threshold"
	labelDefault="Threshold (°C)"
	hintKey={VENTORA_TEMP_THRESHOLD_HINT.key}
	hintDefault={VENTORA_TEMP_THRESHOLD_HINT.default}
	value={draft.thr}
	min={ventoraTempLimits.thr[0]}
	max={ventoraTempLimits.thr[1]}
	icon="fas fa-temperature-half"
	validation={thresholdValidation}
	onInput={(e) => inputField('thr', e)}
	onBlur={(e) => blurField('thr', e)}
/>

<VentoraFanNumericInput
	_id="input-ventora-temp-off-delay"
	labelKey="ventora_temp_off_delay"
	labelDefault="Off delay (seconds)"
	hintKey={VENTORA_TEMP_OFF_DELAY_HINT.key}
	hintDefault={VENTORA_TEMP_OFF_DELAY_HINT.default}
	value={draft.off_delay}
	min={ventoraTempLimits.off_delay[0]}
	max={ventoraTempLimits.off_delay[1]}
	icon="fas fa-hourglass-end"
	validation={offDelayValidation}
	onInput={(e) => inputField('off_delay', e)}
	onBlur={(e) => blurField('off_delay', e)}
/>

<Button
	size="s"
	width="full"
	variant="primary"
	disabled={!dirty || hasValidationErrors}
	{loading}
	on:click={save}
	id="button-ventora-temp-trigger-save"
>
	{$format('button_save', { default: 'Save' })}
</Button>
